import { api } from '../../../lib/api';

export const maintPlanService = {
  /**
   * Get all maintenance plans with filtering and pagination
   * @param {Object} filters - Filter criteria (factory_code, asset_id, maintenance_type, etc.)
   * @param {number} page - Page number (1-indexed)
   * @param {number} pageSize - Number of items per page (0 for all)
   * @returns {Promise<Object>} Response with data array and pagination info
   */
  async getAll(filters = {}, page = 1, pageSize = 10) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('page_size', pageSize);

      // Add filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });

      const url = `/api/maintenance/plans?${queryParams.toString()}`;
      const data = await api.get(url);
      return data || { data: [], total: 0 };
    } catch (error) {
      console.error('Error fetching maintenance plans:', error);
      throw error;
    }
  },

  /**
   * Get a specific maintenance plan by ID
   * @param {number} planId - The plan ID
   * @returns {Promise<Object>} The maintenance plan with procedures and spare parts
   */
  async getById(planId) {
    try {
      const data = await api.get(`/api/maintenance/plans/${planId}`);
      return data || null;
    } catch (error) {
      console.error(`Error fetching maintenance plan ${planId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new maintenance plan
   * @param {Object} planData - Plan data (name, asset_id, maintenance_type, priority, etc.)
   * @returns {Promise<Object>} The created maintenance plan
   */
  async create(planData) {
    try {
      const data = await api.post('/api/maintenance/plans', planData);
      return data;
    } catch (error) {
      console.error('Error creating maintenance plan:', error);
      throw error;
    }
  },

  /**
   * Update an existing maintenance plan
   * @param {number} id - The plan ID
   * @param {Object} updateData - Updated plan data
   * @returns {Promise<Object>} The updated maintenance plan
   */
  async update(id, updateData) {
    try {
      const data = await api.put(`/api/maintenance/plans/${id}`, updateData);
      return data;
    } catch (error) {
      console.error(`Error updating maintenance plan ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a maintenance plan
   * @param {number} id - The plan ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      await api.delete(`/api/maintenance/plans/${id}`);
    } catch (error) {
      console.error(`Error deleting maintenance plan ${id}:`, error);
      throw error;
    }
  },

  async complete(id) {
    try {
      const data = await api.put(`/api/maintenance/plans/${id}/complete`);
      return data;
    } catch (error) {
      console.error(`Error completing maintenance plan ${id}:`, error);
      throw error;
    }
  }
};
